import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, SectionLabel, scrollToId } from "./Reveal";
import { IMAGES } from "../../data";
import { useLang } from "../../i18n";

const SERVICE_IMAGES = [IMAGES.forklift, IMAGES.aerial, IMAGES.training];
const SERVICE_IDS = ["forklift", "aerial", "trainer"];

export const Services = () => {
  const { t } = useLang();
  const s = t.services;
  return (
    <section
      id="programs"
      data-testid="services-section"
      className="relative py-24 sm:py-32 bg-[#0a1430] border-y border-white/10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionLabel num="[ 02 ]">{s.label}</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-14">
            <h2 className="font-head font-extrabold uppercase tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.9] max-w-2xl">
              {s.title1}
              <span className="block text-gold-grad">{s.title2}</span>
            </h2>
            <p className="text-slate-400 max-w-sm">{s.intro}</p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-5">
          {s.items.map((item, i) => (
            <Reveal key={SERVICE_IDS[i]} delay={i * 0.08}>
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 300, damping: 24 }}
                className="group h-full flex flex-col rounded-2xl overflow-hidden card-hairline hover:border-amber-400/40 transition-colors"
                data-testid={`service-card-${SERVICE_IDS[i]}`}
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={SERVICE_IMAGES[i]}
                    alt={item.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f1c40] via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 font-mono-x text-[10px] uppercase tracking-[0.2em] text-amber-400 bg-[#060c1f]/70 backdrop-blur px-3 py-1.5 rounded-full border border-white/10">
                    {item.tag}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-head font-bold uppercase tracking-wide text-2xl text-white leading-tight">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {item.points.map((p) => (
                      <li key={p} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check size={16} className="text-amber-400 mt-0.5 shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => scrollToId("quote")}
                    data-testid={`service-cta-${SERVICE_IDS[i]}`}
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-head font-bold uppercase tracking-wide text-white group-hover:text-amber-400 transition-colors"
                  >
                    {s.requestCta}
                    <ArrowUpRight size={16} />
                  </button>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-8 rounded-2xl card-hairline p-7 sm:p-9">
            <h3 className="font-head font-bold uppercase tracking-wide text-xl text-white mb-6">
              {s.includesTitle}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
              {s.includes.map((c) => (
                <div key={c} className="flex items-center gap-3 text-slate-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                  <span className="text-sm">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
